import React, { useState } from 'react';
import { Modal, DatePicker, Progress, Alert, Typography } from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { useSyncUserBank } from '../hooks/useFetch';

const { RangePicker } = DatePicker;
const { Text } = Typography;

/**
 * แบ่งช่วงที่เลือกเป็นก้อนละ 1 วัน
 *
 * ทำไมต้องถี่ขนาดนี้: เว็บ MGB ส่ง HTML กลับมาทั้งก้อนไม่มีแบ่งหน้า
 * และปริมาณต่อวันไม่แน่นอนเลย วันปกติ ~440 คน (0.4 MB) แต่วันที่มีการ
 * นำเข้าสมาชิกยกล็อต เช่น 2026-06-01 = 38,689 คน (118 MB) กับ 06-02 อีก ~33,000 คน
 *
 * เดิมแบ่งทีละเดือน แล้วเดือน มิ.ย. เจอสองวันนั้นรวมกันทีเดียว = ทะลุ 200 MB
 * ต่อ 1 request จน backend ตัดทิ้ง — แบ่งรายวันแล้วก้อนใหญ่สุดถูกจำกัดที่ "1 วัน"
 * ไม่ว่าข้อมูลจะกระจุกตรงไหน
 */
const buildChunks = (from, to) => {
  const chunks = [];
  let cursor = from.startOf('day');
  const last = to.startOf('day');
  while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
    const day = cursor.format('YYYY-MM-DD');
    chunks.push({ from: day, to: day });
    cursor = cursor.add(1, 'day');
  }
  return chunks;
};

const emptyTotals = { inserted: 0, updated: 0, filled: 0, unchanged: 0, members: 0, unparsedGameUser: 0 };

function SyncModal({ open, onClose, onFinished }) {
  const { currentColor } = useStateContext();
  const { mutateAsync: syncUserBank } = useSyncUserBank();

  // เลือกเป็น "เดือน/ปี" ไม่ต้องเลือกวัน — ค่าเริ่มต้น = 5 เดือนก่อน ถึงเดือนนี้
  const [range, setRange] = useState([dayjs().subtract(5, 'month'), dayjs()]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [current, setCurrent] = useState('');
  const [totals, setTotals] = useState(emptyTotals);
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);

  const reset = () => {
    setRunning(false);
    setDone(0);
    setTotalChunks(0);
    setCurrent('');
    setTotals(emptyTotals);
    setError('');
    setFinished(false);
  };

  const handleClose = () => {
    if (running) return; // กำลังดึงอยู่ ห้ามปิด เดี๋ยวดึงค้างกลางทาง
    reset();
    onClose();
  };

  const handleStart = async () => {
    if (!range?.[0] || !range?.[1]) {
      setError('กรุณาเลือกช่วงเดือน');
      return;
    }
    // เลือกมาเป็นเดือน → กางเป็นวันที่ 1 ของเดือนแรก ถึงวันสุดท้ายของเดือนหลัง
    // ถ้าเดือนหลังคือเดือนปัจจุบัน ตัดที่วันนี้พอ ไม่ต้องขอวันที่ยังมาไม่ถึง
    const today = dayjs();
    const from = range[0].startOf('month');
    let to = range[1].endOf('month');
    if (to.isAfter(today)) to = today;

    if (to.isBefore(from, 'day')) {
      setError('เดือนสิ้นสุดต้องไม่น้อยกว่าเดือนเริ่มต้น');
      return;
    }

    const chunks = buildChunks(from, to);
    if (!chunks.length) {
      setError('ช่วงเดือนไม่ถูกต้อง');
      return;
    }

    setError('');
    setFinished(false);
    setRunning(true);
    setDone(0);
    setTotalChunks(chunks.length);
    const sum = { ...emptyTotals };
    setTotals(sum);

    try {
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        setCurrent(chunk.from);
        // ยิงทีละช่วง ไม่ยิงพร้อมกัน — เว็บปลายทางเป็นระบบจริงที่คนอื่นใช้อยู่
        const { result } = await syncUserBank(chunk);
        sum.inserted += result?.inserted || 0;
        sum.updated += result?.updated || 0;
        sum.filled += result?.filled || 0;
        sum.unchanged += result?.unchanged || 0;
        sum.members += result?.members || 0;
        sum.unparsedGameUser += result?.unparsedGameUser || 0;
        setTotals({ ...sum });
        setDone(i + 1);
      }
      setFinished(true);
      setCurrent('');
      onFinished?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'ดึงข้อมูลไม่สำเร็จ');
    } finally {
      setRunning(false);
    }
  };

  const percent = totalChunks ? Math.round((done / totalChunks) * 100) : 0;

  return (
    <Modal
      title="ดึงข้อมูลสมาชิกจากเว็บ MGB"
      open={open}
      onOk={handleStart}
      onCancel={handleClose}
      okText={finished ? 'ดึงอีกครั้ง' : 'เริ่มดึงข้อมูล'}
      cancelText="ปิด"
      width={620}
      maskClosable={!running}
      closable={!running}
      okButtonProps={{
        loading: running,
        icon: <CloudDownloadOutlined />,
        style: { backgroundColor: currentColor, borderColor: currentColor },
      }}
      cancelButtonProps={{ disabled: running }}
    >
      <div style={{ color: '#212121' }}>
        <p className="mb-2">
          ดึงรายชื่อสมาชิกตาม <b>วันที่สมัคร</b> จากหน้า “เช็คข้อมูลสมาชิก” ของเว็บ MGB
          มาเพิ่ม/อัปเดตในตารางนี้
        </p>

        <div className="mb-3">
          <div className="mb-1 text-sm font-medium">ช่วงเดือนที่สมัคร</div>
          <RangePicker
            picker="month"
            value={range}
            onChange={setRange}
            format="MM/YYYY"
            allowClear={false}
            disabled={running}
            disabledDate={(d) => d && d.isAfter(dayjs(), 'month')}
            style={{ width: '100%' }}
          />
          <Text type="secondary" className="text-xs">
            เลือกเป็นเดือน ระบบจะดึงตั้งแต่วันที่ 1 ของเดือนแรก ถึงสิ้นเดือนหลัง
            (เดือนปัจจุบันดึงถึงวันนี้) แล้วแบ่งยิงทีละวันให้เอง — 5 เดือนใช้เวลาประมาณ 3-5 นาที อย่าเพิ่งปิดหน้าต่าง
          </Text>
        </div>

        {(running || finished) && (
          <div className="mb-3">
            <Progress
              percent={percent}
              status={error ? 'exception' : finished ? 'success' : 'active'}
              strokeColor={currentColor}
            />
            <div className="text-sm">
              วันที่ {done}/{totalChunks} {current ? `· กำลังดึง ${current}` : ''}
            </div>
            <div className="text-sm mt-1">
              เพิ่มใหม่ <b>{totals.inserted.toLocaleString()}</b> ·
              อัปเดต <b>{totals.updated.toLocaleString()}</b> ·
              {/* แถวที่เดิมช่องว่างแล้วเพิ่งมีค่า — ตัวเลขที่ใช้ดูว่าไล่เติมข้อมูลย้อนหลังคืบไปแค่ไหน */}
              เติมข้อมูลเดิม <b>{totals.filled.toLocaleString()}</b> ·
              เหมือนเดิม <b>{totals.unchanged.toLocaleString()}</b> ·
              อ่านจากเว็บทั้งหมด <b>{totals.members.toLocaleString()}</b> คน
            </div>
          </div>
        )}

        {/* ช่อง BETFLIX SPORTBOOK ไม่ว่างแต่แยกค่ายไม่ออก = หน้าเว็บอาจเปลี่ยนรูปแบบ ต้องรู้ทันที */}
        {totals.unparsedGameUser > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-2"
            message={`มี ${totals.unparsedGameUser.toLocaleString()} แถวที่อ่านช่อง BETFLIX SPORTBOOK ไม่ออก — รูปแบบบนเว็บอาจเปลี่ยน`}
          />
        )}

        {error && <Alert type="error" showIcon message={error} className="mb-2" />}
        {finished && !error && (
          <Alert type="success" showIcon message="ดึงข้อมูลครบทุกช่วงแล้ว" className="mb-2" />
        )}
      </div>
    </Modal>
  );
}

export default SyncModal;
